import { TranslationFunctionType } from '@/common/types';
import moment from 'moment';
import { array, date, number, object, string } from 'yup';

const parseDate = (value: Date | null, originalValue: Date | null) => {
  if (originalValue) {
    return moment(originalValue, true).seconds(0).millisecond(0).toDate();
  }

  return null;
};

export const launchCampaignSchema = (t: TranslationFunctionType) =>
  object().shape(
    {
      emailTemplate: string().uuid().required(t('error.select.emailTemplate')),
      landingPage: string().uuid().required(t('error.select.landingPage')),
      targets: array().min(1, t('error.select.oneTargetGroup')),
      excludes: array(),
      sendingProfile: object().required(t('error.select.sender')),
      periodType: string().equals(['drip', 'blast']),
      launchDate: date()
        .required(t('error.select.launch'))
        .transform(parseDate),
      completeAfter: date()
        .nullable()
        .transform(parseDate)
        .when(
          ['periodType', 'launchDate', 'sendByDate'],
          ([periodType, launchDate, sendByDate], schema) => {
            if (periodType === 'drip' && sendByDate) {
              return schema.min(
                moment(sendByDate),
                t('error.select.pastCompleteAfterSendDate'),
              );
            }
            if (launchDate) {
              return schema.min(
                moment(launchDate),
                t('error.select.pastCompleteAfterStart'),
              );
            }
            return schema;
          },
        ),
      sendByDate: date()
        .nullable()
        .transform(parseDate)
        .when(
          ['periodType', 'launchDate'],
          ([periodType, launchDate], schema) =>
            periodType === 'drip' && launchDate
              ? schema
                  .required(t('error.select.sendDate'))
                  .min(moment(launchDate), t('error.select.pastLaunch'))
              : schema.notRequired(),
        ),
      name: string().required(t('error.select.name')),
    },
    [
      ['sendByDate', 'periodType'],
      ['sendByDate', 'launchDate'],
    ],
  );

export const saveCampaignTemplateSchema = (t: TranslationFunctionType) =>
  object().shape({
    emailTemplate: string().uuid().required(t('error.select.emailTemplate')),
    landingPage: string().uuid().required(t('error.select.landingPage')),
    sendingProfile: object().required(t('error.select.sender')),
    name: string().required(t('error.select.name')),
    language: string().required(t('error.requiredLanguage')),
    masterTemplate: string().nullable().notRequired(),
    difficulty: number()
      .oneOf([1, 2, 3, 4, 5])
      .when('masterTemplate', {
        is: (masterTemplate?: string | null) => !masterTemplate,
        then: (schema) => schema.required(t('error.requiredDifficulty')),
        otherwise: (schema) => schema.notRequired(),
      }),
    clickedLinksPercentage: number()
      .nullable()
      .min(1, t('error.OneToOneHundredNumber'))
      .max(100, t('error.OneToOneHundredNumber')),
    submittedDataPercentage: number()
      .nullable()
      .min(1, t('error.OneToOneHundredNumber'))
      .max(100, t('error.OneToOneHundredNumber')),
  });
